import { IconClock, IconClockPause } from "@tabler/icons-react";

import { ApiError } from "../api/client";
import { useRelay } from "../api/queries";
import type { Relay, RelayJeanZay, RelayMlflow } from "../api/types";
import { useSession } from "../auth/SessionProvider";
import { SshKeyForm } from "../components/account/SshKeyForm";
import { PageHeader } from "../components/layout/PageHeader";
import { CopyBlock } from "../components/ui/CopyBlock";
import { ErrorBlock, LoadingBlock } from "../components/ui/StateBlock";

/**
 * The title `docs/PRODUCT.md` gives this section, word for word.
 *
 * The sidebar says "Training elsewhere" because a menu entry has to be short;
 * the screen says what it is in full, because that sentence is the product
 * explaining itself.
 */
const SECTION_TITLE =
  "Training elsewhere: our MLflow from any cluster, and the door to Jean Zay";

/**
 * The sentence every state of this screen carries.
 *
 * The console publishes what it holds; the machine fetches it about once a
 * minute and the console does not watch it do so (`docs/AUTH.md` §4b). Saying
 * "you can connect" without saying that is how somebody reads a green screen,
 * tries immediately, is refused, and concludes the console lies.
 */
const SYNC_NOTE =
  "That machine asks the console for the list of keys about once a minute, " +
  "so a key you just saved — or an approval that just came through — lands " +
  "there about a minute later. The console does not watch it happen: if the " +
  "first attempt is refused, wait a minute and try again.";

/**
 * The machine exists on paper and not yet in the world.
 *
 * Deliberately **not** the cluster screen: its checklist is about the cluster
 * this console manages, and this machine is not in it. What is missing here is
 * one address, and inventing one is the thing the product must never do.
 *
 * Args:
 *     message: What the server said, quoted as it said it.
 *
 * Returns:
 *     The being-prepared panel.
 */
function BeingPrepared({ message }: { message: string }): JSX.Element {
  return (
    <section
      className="problem problem--calm"
      role="status"
      aria-labelledby="relay-waiting"
    >
      <div className="problem__head">
        <IconClockPause size={20} aria-hidden="true" />
        <h2 className="problem__title" id="relay-waiting">
          The machine is still being prepared
        </h2>
      </div>

      <p className="problem__lead">
        Training somewhere else goes through one machine of ours, and the
        console has not been told its address yet. So there is nothing to paste
        here — and no address is shown that was not given, which is why this
        page is a sentence rather than a guess. Nothing is wrong with your
        account.
      </p>

      <p className="problem__lead">
        A key of yours marked for training elsewhere is what will open it. If
        one is on file, nothing is asked of you — when the machine exists, this
        screen shows the lines to run. If none is, add one on your account
        screen while you wait.
      </p>

      <div className="problem__quote">
        <span className="problem__quote-label">The server said</span>
        <p className="problem__quote-text">{message}</p>
      </div>
    </section>
  );
}

/**
 * The everyday half: our MLflow on the run's own `localhost`.
 *
 * Both lines are shown exactly as the server published them and are never
 * rebuilt here. The far end of the forward is that server's configuration; a
 * console that assembled its own line would be inventing an address the moment
 * the configuration changed.
 *
 * Args:
 *     mlflow: The tunnel as the server published it.
 *
 * Returns:
 *     The two copy-paste blocks, and why the flag in the first one is there.
 */
function MlflowTunnel({ mlflow }: { mlflow: RelayMlflow }): JSX.Element {
  return (
    <div className="connect">
      <p className="section__lead">
        Run this once on the machine your training is on. It opens the tunnel
        and stays in the background.
      </p>
      <CopyBlock
        value={mlflow.tunnel_command}
        label="Copy the tunnel command"
        multiline
      />

      <p className="connect__hint">
        <span className="mono">ExitOnForwardFailure</span> is in that line on
        purpose. Without it <span className="mono">ssh</span> reports success
        even when the tunnel could not be opened, and the run then logs into
        nothing: the training finishes and the experiment is gone. With it the
        command fails immediately, and you know before you start.
      </p>

      <div className="connect__config">
        <p className="section__lead">
          Then point the run at it, in the same shell, before you launch.
        </p>
        <CopyBlock
          value={mlflow.export_command}
          label="Copy the tracking URI export"
          multiline
        />
        <p className="connect__hint">
          Your runs log to{" "}
          <span className="mono">{mlflow.tracking_uri}</span> from there, which
          is the tunnel, which is the team's MLflow. Nothing else in your code
          changes.
        </p>
      </div>
    </div>
  );
}

/**
 * A plain shell on that machine, for everybody who has one.
 *
 * Top level rather than tucked inside the Jean Zay half, and deliberately so:
 * every approved researcher now has an account there — that is the whole point
 * of this section — and the plain shell is the first thing anybody reaches for
 * when a tunnel misbehaves. `docs/API.md` says exactly that about
 * `ssh_command`, and it carries both fields at the top level of the payload
 * rather than inside `jean_zay`, which is the contract saying where they
 * belong. Hiding the console's own diagnostic from most of the people who will
 * need it would be backwards.
 *
 * Both lines are the server's, shown as it published them.
 *
 * Args:
 *     relay: The section as the server published it.
 *
 * Returns:
 *     The `ssh` line and the `~/.ssh/config` stanza, or the reason there is
 *     nothing to show yet.
 */
function MachineShell({ relay }: { relay: Relay }): JSX.Element {
  return (
    <section className="section" aria-labelledby="relay-machine">
      <h2 className="section__title" id="relay-machine">
        The machine itself
      </h2>
      <p className="section__lead">
        A plain shell on that machine, under the account name you already have
        here. It is also the first thing to try when a tunnel misbehaves: if
        this line works and the tunnel does not, the problem is the forward and
        not your access.
      </p>

      {relay.key_on_file ? (
        <div className="connect">
          <CopyBlock
            value={relay.ssh_command}
            label="Copy the SSH command"
            multiline
          />

          <p className="connect__hint">
            You are <span className="mono">{relay.username}</span> over there
            too — the same name as here — on{" "}
            <span className="mono">{relay.host}</span>, port {relay.port}.
          </p>

          <div className="connect__config">
            <p className="connect__hint">
              Or add this to <span className="mono">~/.ssh/config</span> once,
              and <span className="mono">ssh relay</span> is enough afterwards.
              There is one machine and its alias never changes, so pasting the
              stanza twice is harmless.
            </p>
            <CopyBlock
              value={relay.ssh_config}
              label="Copy the SSH config entry"
              multiline
            />
          </div>
        </div>
      ) : (
        <p className="state-block">
          The <span className="mono">ssh</span> line appears here as soon as a
          key is on file. Without one there is nothing to install on that
          machine, so it would be refused.
        </p>
      )}
    </section>
  );
}

/**
 * The second half, for the people the team has declared to IDRIS.
 *
 * Kept as a section of its own rather than folded into a paragraph above: it is
 * a different institution and a different account, it is the one part of this
 * screen most people never see, and a heading is what a declared researcher
 * scans for. It carries no connection block — the shell above is the shell, and
 * repeating it would suggest a second way in that does not exist.
 *
 * Args:
 *     jeanZay: The record: who declared this account to IDRIS.
 *
 * Returns:
 *     The Jean Zay half of the instructions.
 */
function JeanZayHalf({ jeanZay }: { jeanZay: RelayJeanZay }): JSX.Element {
  return (
    <section className="section" aria-labelledby="relay-jean-zay">
      <h2 className="section__title" id="relay-jean-zay">
        Jean Zay
      </h2>
      <p className="section__lead">
        The team has declared you to IDRIS, so that same machine is also your
        route to the national supercomputer: it is the one address they accept
        our connections from. Get a shell on it with the connection line this
        page shows once a key of yours is on file, and go on from there.
      </p>
      <p className="section__lead">
        You go on with your <strong>own</strong> IDRIS account. That account is
        yours and not the console's: it does not create it, does not know your
        login there and cannot reset it. Nothing about what runs at the national
        centre appears here either — their tools report that, not this one.
      </p>
      <p className="aside__meta">Declared to IDRIS by {jeanZay.granted_by}.</p>
    </section>
  );
}

/**
 * *Training elsewhere*: our MLflow from any cluster, and the door to Jean Zay.
 *
 * Every approved researcher sees this screen. Reaching that machine comes with
 * approval, exactly like the GPUs do, and nothing here is refused for want of
 * a second permission (`docs/AUTH.md` §2, ADR-0011). What varies is what the
 * payload carries: the MLflow half is absent when the deployment has no MLflow
 * address, and the Jean Zay half only appears for the accounts the team has
 * declared to IDRIS. The shell on the machine is everybody's, like the machine.
 *
 * States, in this order: the machine has no address yet; the answer is still
 * coming; here are the lines to paste.
 *
 * Returns:
 *     The *Training elsewhere* screen.
 */
export function RelayPage(): JSX.Element {
  const { me } = useSession();
  const relay = useRelay();

  const header = (
    <PageHeader
      eyebrow="Access"
      title={SECTION_TITLE}
      subtitle="One machine of ours, outside the cluster, on both sides of the private network."
      back={{ to: "/", label: "GPUs" }}
    />
  );

  const error = relay.error;

  if (error instanceof ApiError && error.isNotConnected) {
    return (
      <>
        {header}
        <BeingPrepared message={error.message} />
      </>
    );
  }

  if (relay.isPending) {
    return (
      <>
        {header}
        <LoadingBlock label="Reading how to reach the machine…" />
      </>
    );
  }

  if (relay.isError) {
    return (
      <>
        {header}
        <ErrorBlock error={relay.error} onRetry={() => void relay.refetch()} />
      </>
    );
  }

  const section = relay.data;

  return (
    <>
      {header}

      <section className="state-line" data-tone={section.ready ? "ok" : "pending"}>
        <span className="badge" data-tone={section.ready ? "ok" : "pending"}>
          <span className="badge__dot" aria-hidden="true" />
          {section.ready ? "Ready to connect" : "Waiting for your key"}
        </span>
        <p className="state-line__text">
          {section.ready
            ? "You may log into that machine with a key from your account marked for training elsewhere — being approved here is all it takes."
            : "No key on your account is marked for training elsewhere, so there is nothing to install for you over there. Add one below, or widen a key you already have on your account screen."}
        </p>
      </section>

      {section.key_on_file ? null : (
        <section className="section" aria-labelledby="relay-key">
          <h2 className="section__title" id="relay-key">
            Your SSH key
          </h2>
          <p className="section__lead">
            A key saved with <em>Training elsewhere</em> ticked is installed
            over there at its next sync. There is nothing separate to register
            — one list of keys, each saying where it may go.
          </p>
          <SshKeyForm
            existingKeys={me.ssh_public_keys}
            title="Add a key for training elsewhere"
            onSaved={() => void relay.refetch()}
          />
        </section>
      )}

      <section className="section" aria-labelledby="relay-mlflow">
        <h2 className="section__title" id="relay-mlflow">
          MLflow from a compute cluster
        </h2>
        <p className="section__lead">
          Our MLflow sits on a private address that nothing outside our network
          resolves. A tunnel through this machine puts it on the{" "}
          <span className="mono">localhost</span> of whatever machine your run
          is on, and the run logs where the team reads it.
        </p>

        {section.mlflow === null ? (
          <p className="state-block">
            The console has not been told where MLflow answers, so there is no
            tunnel to show here. An address guessed for you would be a run that
            logs into nothing, which is worse than this sentence — ask whoever
            runs the console to configure it.
          </p>
        ) : section.key_on_file ? (
          <MlflowTunnel mlflow={section.mlflow} />
        ) : (
          <p className="state-block">
            The tunnel appears here as soon as a key is on file. Without one
            there is nothing to install on that machine, so the command would be
            refused.
          </p>
        )}
      </section>

      <MachineShell relay={section} />

      {section.jean_zay === null ? null : (
        <JeanZayHalf jeanZay={section.jean_zay} />
      )}

      <p className="notice">
        <IconClock size={14} aria-hidden="true" />
        <span>{SYNC_NOTE}</span>
      </p>

      <section className="section" aria-labelledby="relay-scope">
        <h2 className="section__title" id="relay-scope">
          What this is not
        </h2>
        <p className="section__lead">
          None of this is GPU capacity the console hands out. There is no
          workspace to start here, no deadline, no idle countdown, no GPU
          activity to watch and no <span className="mono">/home</span> of ours
          over there. The console does not run that machine and reports nothing
          about what happens on it or at the national centre. Reaching it takes
          nothing from the GPUs on this console either.
        </p>
      </section>
    </>
  );
}
